import { SchoolContentItem } from "@axl-dev/school-sdk";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSchool, useSdk } from "@app/shared/lib/hooks";

import { SchoolContentSectionType } from "./school-content-item";

export enum SchoolCourseType {
  Started = "started",
  Available = "available",
  All = "all",
}

export enum SchoolContentItemType {
  Folder = "folder",
  Item = "item",
}

type SchoolContentItemFilter = {
  courseType: SchoolCourseType;
  onlyAvailable: boolean;
  sectionType: SchoolContentSectionType;
  search: string;
};

type CoursesContextType = {
  filter: Partial<SchoolContentItemFilter>;
  onFilterItemChange: <T extends keyof SchoolContentItemFilter>(
    item: T,
    value: SchoolContentItemFilter[T],
  ) => void;
  items: SchoolContentItem[];
  isLoading: boolean;
};

const CoursesContext = createContext<CoursesContextType>(
  {} as CoursesContextType,
);

export const useCourses = () => useContext(CoursesContext);

export const CoursesProvider: FC<PropsWithChildren> = ({ children }) => {
  const school = useSchool();
  const sdk = useSdk();
  const [isLoading, setLoading] = useState(true);
  const [items, setItems] = useState<SchoolContentItem[]>([]);
  const [filter, setFilter] = useState<Partial<SchoolContentItemFilter>>({
    courseType: SchoolCourseType.All,
    sectionType: SchoolContentSectionType.Course,
    onlyAvailable: school.showUnavailableStartedCourses !== true,
  });
  useEffect(() => {
    (async () => {
      setLoading(true);
      sdk.content.courses.store.updateFilter(filter);
      await sdk.content.courses.store.fetch();
      setItems(sdk.content.courses.store.items);
      setLoading(false);
    })();
  }, [filter, sdk.content.courses]);

  const onItemChange = useCallback(
    <T extends keyof SchoolContentItemFilter>(
      item: T,
      value: SchoolContentItemFilter[T],
    ) => {
      setFilter((prev) => ({ ...prev, [item]: value }));
    },
    [],
  );

  return (
    <CoursesContext.Provider
      value={useMemo(
        () => ({ filter, onFilterItemChange: onItemChange, items, isLoading }),
        [filter, onItemChange, items, isLoading],
      )}
    >
      {children}
    </CoursesContext.Provider>
  );
};
